import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ApplicantDetails } from '../types/Applicant';
import { applicantApi, ApplicantApiError } from '../utils/applicantApi';
import { isAuthOrRoleFailure } from '../utils/authFailure';
import { clearApplicantSession, extractApplicantAccessToken, extractApplicantRefreshToken, getApplicantAccessToken, setApplicantTokens } from '../utils/applicantAuth';

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
const APPLICANT_PORTAL_ACCESS_DENIED_MESSAGE = 'Your agency/principal does not allow their applicants to have access to the online applicant portal. Please ask the agency directly for your medical status.';

const isAccessDeniedValue = (value: unknown) => {
  return value === false || value === 0 || value === '0' || value === 'false';
};

const getApplicantPayload = (payload: any) => {
  return payload?.applicant || payload?.data?.applicant || payload?.medical_info || payload?.data || payload || {};
};

const isApplicantPortalAccessDenied = (payload: any) => {
  const applicantPayload = getApplicantPayload(payload);
  const applicationInfo = applicantPayload?.application_info || {};

  const accessValues = [
    applicantPayload?.allow_applicant_portal_access,
    applicantPayload?.agency_allow_applicant_portal_access,
    applicantPayload?.principal_allow_applicant_portal_access,
    applicantPayload?.agency?.allow_applicant_portal_access,
    applicantPayload?.principal?.allow_applicant_portal_access,
    applicationInfo?.agency_allow_applicant_portal_access,
    applicationInfo?.principal_allow_applicant_portal_access,
    applicationInfo?.agency?.allow_applicant_portal_access,
    applicationInfo?.principal?.allow_applicant_portal_access,
    payload?.agency?.allow_applicant_portal_access,
    payload?.principal?.allow_applicant_portal_access,
  ];

  return accessValues.some(isAccessDeniedValue);
};

interface ApplicantAuthContextType {
  applicant: ApplicantDetails | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, clinic: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshApplicantDetails: () => Promise<void>;
}

const ApplicantAuthContext = createContext<ApplicantAuthContextType | undefined>(undefined);

export const useApplicantAuth = () => {
  const context = useContext(ApplicantAuthContext);
  if (!context) {
    throw new Error('useApplicantAuth must be used within an ApplicantAuthProvider');
  }
  return context;
};

interface ApplicantAuthProviderProps {
  children: ReactNode;
}

export const ApplicantAuthProvider: React.FC<ApplicantAuthProviderProps> = ({ children }) => {
  const [applicant, setApplicant] = useState<ApplicantDetails | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleForcedLogout = useCallback(() => {
    setApplicant(null);
    setToken(null);
    clearApplicantSession();
  }, []);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedToken = getApplicantAccessToken();

    if (savedToken) {
      setToken(savedToken);
      // Always clear stale cached dashboard payload and force API refresh.
      localStorage.removeItem('applicantInfo');
    }

    setIsLoading(false);
  }, []);

  const login = async (username: string, clinic: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsLoading(true);

      const response = await fetch(`${VITE_API_BASE_URL}/applicant/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          username,
          clinic,
          password
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
        return { success: false, error: errorData.message || 'Login failed' };
      }

      const data = await response.json();

      if (isApplicantPortalAccessDenied(data)) {
        clearApplicantSession();
        setToken(null);
        setApplicant(null);
        return { success: false, error: APPLICANT_PORTAL_ACCESS_DENIED_MESSAGE };
      }

      const accessToken = extractApplicantAccessToken(data);
      const refreshToken = extractApplicantRefreshToken(data);
      if (!accessToken) {
        return { success: false, error: 'Login succeeded but no access token was returned.' };
      }

      // Store token and applicant info
      setToken(accessToken);
      setApplicant(data.applicant);

      setApplicantTokens(accessToken, refreshToken);
      localStorage.setItem('applicantInfo', JSON.stringify(data.applicant));

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      if (token) {
        // Call logout endpoint
        await applicantApi.post('/logout', {});
      }
    } catch (error) {
      // Handle logout error silently
    } finally {
      handleForcedLogout();
    }
  }, [token, handleForcedLogout]);

  const refreshApplicantDetails = useCallback(async () => {
    try {
      if (!token) return;

      const response = await applicantApi.get('/dashboard');

      // Handle the nested response structure from backend
      if (response.success && response.medical_info) {
        // Flatten the nested structure to match frontend expectations
        const flattenedData = {
          ...response.medical_info,
          // Merge personal_info fields to top level
          ...(response.medical_info.personal_info || {}),
          // Merge application_info fields to top level
          ...(response.medical_info.application_info || {}),
          // Merge medical_status_info fields to top level
          ...(response.medical_info.medical_status_info || {}),
        };

        setApplicant(flattenedData);
        localStorage.setItem('applicantInfo', JSON.stringify(flattenedData));
      } else {
        // Fallback for different response structure
        setApplicant(response);
        localStorage.setItem('applicantInfo', JSON.stringify(response));
      }
    } catch (error) {
      if ((error instanceof ApplicantApiError && error.isTokenExpired) || isAuthOrRoleFailure(error)) {
        // Token expired/invalid or role mismatch, force immediate logout cleanup.
        handleForcedLogout();
      }

      throw error;
    }
  }, [token, handleForcedLogout]);

  const value: ApplicantAuthContextType = {
    applicant,
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    logout,
    refreshApplicantDetails
  };

  return (
    <ApplicantAuthContext.Provider value={value}>
      {children}
    </ApplicantAuthContext.Provider>
  );
};